import MakeModelAPI, { MakeModelAPIError } from 'api/makeModel.api';
import {
  type MakeModelDetailsResponse,
  type MakeModelUpdatePayload,
  type MakeModelDetails,
} from 'types/makeModel.types';

/**
 * Service layer for Make Model operations
 * Provides a clean interface between components and the API layer
 */

/**
 * Numeric fields that require type conversion
 */
const NUMERIC_FIELDS = [
  'idle_rpm',
  'max_payload_capacity',
  'current_capacity',
] as const;

/**
 * Validation error interface
 */
export interface ValidationError {
  field: string;
  message: string;
}

/**
 * Validation result interface
 */
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

/**
 * Raw API response interface (what the API actually returns)
 * Note: current_capacity comes as string from API but needs conversion to number
 */
interface MakeModelDetailsRaw {
  customerId: string;
  id: string;
  make: string;
  model: string;
  idle_rpm: number | null;
  idle_threshold: number;
  excess_idle_threshold: number;
  fuel_source: string | null;
  max_payload_capacity: number | null;
  current_capacity: string | null; // API returns this as string
  patternName: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Convert raw API response to expected format
 * @param rawData - Raw API response data
 * @returns Processed make model details
 */
function convertRawToProcessed(
  rawData: MakeModelDetailsRaw[]
): MakeModelDetails[] {
  return rawData.map((item) => ({
    ...item,
    current_capacity: convertCapacityValue(item.current_capacity),
  }));
}

/**
 * Convert capacity value from string to number
 * @param value - String value from API
 * @returns Number or null
 */
function convertCapacityValue(value: string | null): number | null {
  if (value === null || value === '') {
    return null;
  }

  const numValue = Number(value);
  return isNaN(numValue) ? null : numValue;
}

/**
 * Validate if a value is a valid number
 * @param value - Value to validate
 * @returns True if valid number or empty/null
 */
export const isValidNumber = (value: any): boolean => {
  if (value === null || value === undefined || value === '') {
    return true; // Allow empty values
  }

  const numValue = Number(value);
  return !isNaN(numValue) && isFinite(numValue);
};

/**
 * Convert value to number and validate
 * @param value - Value to convert
 * @returns Converted number or null
 * @throws {Error} If value is invalid
 */
export const convertToNumber = (value: any): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numValue = Number(value);
  if (isNaN(numValue) || !isFinite(numValue)) {
    throw new Error('Invalid number format');
  }

  return numValue;
};

/**
 * Validate make model data
 * @param data - Make model data to validate
 * @returns Validation result
 */
export const validateMakeModelData = (
  data: Partial<MakeModelDetails>
): ValidationResult => {
  const errors: ValidationError[] = [];

  // Validate current_capacity
  if (data.current_capacity !== null && data.current_capacity !== undefined) {
    if (!isValidNumber(data.current_capacity)) {
      errors.push({
        field: 'current_capacity',
        message: 'Current capacity must be a valid number',
      });
    }
  }

  // Validate max_payload_capacity
  if (
    data.max_payload_capacity !== null &&
    data.max_payload_capacity !== undefined
  ) {
    if (!isValidNumber(data.max_payload_capacity)) {
      errors.push({
        field: 'max_payload_capacity',
        message: 'Max payload capacity must be a valid number',
      });
    }
  }

  // Validate idle_rpm
  if (data.idle_rpm !== null && data.idle_rpm !== undefined) {
    if (!isValidNumber(data.idle_rpm)) {
      errors.push({
        field: 'idle_rpm',
        message: 'Idle RPM must be a valid number',
      });
    }
  }

  // Capacity relationship validation removed - now handled by confirmation dialog

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validate and convert field value based on field type
 * @param field - Field name
 * @param value - Field value
 * @returns Converted value
 * @throws {Error} If value is invalid
 */
function validateAndConvertValue(
  field: string,
  value: string | number | null
): string | number | null {
  if (NUMERIC_FIELDS.includes(field as (typeof NUMERIC_FIELDS)[number])) {
    if (value === '' || value === null || value === undefined) {
      return null;
    }

    const numValue = Number(value);
    if (isNaN(numValue)) {
      throw new Error(`Invalid number value for ${field}: ${value}`);
    }

    return numValue;
  }

  return value;
}

/**
 * Hook for managing Make Model operations
 * Provides methods to interact with the Make Model API
 */
export const useMakeModelService = () => {
  // Instantiate API inside the hook since BaseAPI uses hooks
  const makeModelAPI = new MakeModelAPI();

  /**
   * Get make model details for a customer
   * @param customerId - Customer ID
   * @returns Promise<MakeModelDetailsResponse>
   * @throws {MakeModelAPIError} When the operation fails
   */
  const getMakeModelDetails = async (
    customerId: string
  ): Promise<MakeModelDetailsResponse> => {
    if (!customerId) {
      throw new Error('Customer ID is required');
    }

    try {
      const rawResult = await makeModelAPI.getMakeModelDetails(customerId);
      return convertRawToProcessed(rawResult);
    } catch (error) {
      if (error instanceof MakeModelAPIError) {
        throw error;
      }
      // Create a new error with the original error message
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      throw new MakeModelAPIError(
        `Failed to fetch make model details: ${errorMessage}`
      );
    }
  };

  /**
   * Update make model details
   * @param id - Make model ID
   * @param field - Field name to update
   * @param value - New field value
   * @returns Promise<MakeModelDetails>
   * @throws {MakeModelAPIError} When the operation fails
   */
  const updateMakeModelDetails = async (
    id: string,
    field: string,
    value: string | number | null
  ): Promise<MakeModelDetails> => {
    if (!id) {
      throw new Error('Make model ID is required');
    }

    if (!field) {
      throw new Error('Field name is required');
    }

    try {
      const convertedValue = validateAndConvertValue(field, value);

      const payload: MakeModelUpdatePayload = {
        [field as keyof MakeModelUpdatePayload]: convertedValue,
      };

      return await makeModelAPI.updateMakeModelDetails(id, payload);
    } catch (error) {
      if (error instanceof MakeModelAPIError) {
        throw error;
      }
      // Create a new error with the original error message
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      throw new MakeModelAPIError(
        `Failed to update make model details: ${errorMessage}`
      );
    }
  };

  /**
   * Update multiple make model fields in a single API call
   * @param id - Make model ID
   * @param updates - Object containing field updates
   * @returns Promise<MakeModelDetails>
   * @throws {MakeModelAPIError} When the operation fails
   */
  const updateMakeModelDetailsBulk = async (
    id: string,
    updates: Partial<MakeModelDetails>
  ): Promise<MakeModelDetails> => {
    if (!id) {
      throw new Error('Make model ID is required');
    }

    // Validate the data before making API call
    const validationResult = validateMakeModelData(updates);
    if (!validationResult.isValid) {
      const errorMessages = validationResult.errors
        .map((err) => err.message)
        .join(', ');
      throw new Error(`Validation failed: ${errorMessages}`);
    }

    try {
      // Convert numeric fields to proper types
      const processedUpdates: MakeModelUpdatePayload = {};

      Object.entries(updates).forEach(([field, value]) => {
        if (NUMERIC_FIELDS.includes(field as (typeof NUMERIC_FIELDS)[number])) {
          try {
            const convertedValue = convertToNumber(value);
            processedUpdates[field as keyof MakeModelUpdatePayload] =
              convertedValue as any;
          } catch (error) {
            throw new Error(`Invalid number value for ${field}: ${value}`);
          }
        } else {
          processedUpdates[field as keyof MakeModelUpdatePayload] =
            value as any;
        }
      });

      return await makeModelAPI.updateMakeModelDetails(id, processedUpdates);
    } catch (error) {
      if (error instanceof MakeModelAPIError) {
        throw error;
      }
      // Create a new error with the original error message
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      throw new MakeModelAPIError(
        `Failed to update make model details: ${errorMessage}`
      );
    }
  };

  return {
    getMakeModelDetails,
    updateMakeModelDetails,
    updateMakeModelDetailsBulk,
  };
};
